import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { ONBOARDING_REDIRECT_TO_AFTER_COMPLETE_KEY } from '@/constants/router';
import { useProfileFcaQuestionnaireEligibility } from '@/hooks/queries/use-profile-fca-questionnaire';
import { useProfileIdentity } from '@/hooks/queries/use-profile-identity';
import { useProfileVerificationInformation } from '@/hooks/queries/use-profile-verification-information';
import { useProfileVerificationInformationSupportingInformation } from '@/hooks/queries/use-profile-verification-information-supporting-information';
import { WORKFLOW_LEVEL_IDENTIFIER } from '@/services/api/profile';
import { AccountType } from '@/utils/account-type';
import LoadingPage from './loading';
import type { OnboardedGuardVerificationPathType } from './onboarded-guard.types';

/**
 * Note: The "/futures" and "/onboarding/verification/status" routes have their own verification paths. As such, we don't want
 * to redirect back to onboarding if the user is on one of these paths AND the information they're missing corresponds to the
 * current path. If information other than verification is missing (or the missing information does not correspond to the
 * current path), we'll still redirect back.
 */

type OnboardedGuardProps = {
  accountType?: AccountType;
  redirectToAfterComplete?: string;
  verificationPaths?: Partial<Record<OnboardedGuardVerificationPathType, string>>;
};

const OnboardedGuard = ({ accountType = AccountType.SPOT, redirectToAfterComplete, verificationPaths }: OnboardedGuardProps) => {
  const { isComplete: isCompleteIdentity, isLoading: isLoadingIdentity, data: identity } = useProfileIdentity();
  const { isLoading: isLoadingFcaQuestionnaire, isComplete: isCompleteFcaQuestionnaire } = useProfileFcaQuestionnaireEligibility(identity?.addressOfResidency.country, accountType);
  const { isLoading: isLoadingVerificationInformation, data: verificationInformation } = useProfileVerificationInformation();
  const isWorkflowVerificationLevel = !!verificationInformation?.verification?.level?.includes(WORKFLOW_LEVEL_IDENTIFIER);
  const { isLoading: isLoadingVerificationInformationSupportingInformation, isComplete: isCompleteVerificationInformationSupportingInformationQuery } =
    useProfileVerificationInformationSupportingInformation(isWorkflowVerificationLevel);
  const location = useLocation();
  const isCompleteVerificationInformation = !!verificationInformation?.verification && verificationInformation.verification.status !== 'NOT_VERIFIED';
  const isCompleteVerificationInformationSupportingInformation = isWorkflowVerificationLevel ? !!isCompleteVerificationInformationSupportingInformationQuery : true;
  const isCompleteAml = !!verificationInformation?.aml.date;
  const isLoading = isLoadingIdentity || isLoadingFcaQuestionnaire || isLoadingVerificationInformation || isLoadingVerificationInformationSupportingInformation;
  const isOnboardedExcludingVerification = !!isCompleteIdentity && isCompleteFcaQuestionnaire && isCompleteAml;
  const isOnboardedVerification = isCompleteVerificationInformation && isCompleteVerificationInformationSupportingInformation;
  const shouldRedirect =
    !isOnboardedExcludingVerification ||
    (!verificationPaths
      ? !isOnboardedVerification
      : (!isCompleteVerificationInformation && location.pathname !== verificationPaths.VERIFICATION && location.pathname !== verificationPaths.INFORMATION) ||
        (!isCompleteVerificationInformationSupportingInformation && location.pathname !== verificationPaths.SUPPORTING_INFORMATION));

  if (isLoading) return <LoadingPage />;

  if (shouldRedirect) {
    if (redirectToAfterComplete) {
      window.sessionStorage.setItem(ONBOARDING_REDIRECT_TO_AFTER_COMPLETE_KEY, redirectToAfterComplete);
    }

    return <Navigate to="/onboarding" replace />;
  }

  return <Outlet />;
};
OnboardedGuard.displayName = 'OnboardedGuard';

export default OnboardedGuard;
